import { Theme } from '@mui/material';
import { format, isToday, isValid, isYesterday } from 'date-fns';
import { TripSummaryDto } from '@acorn-portal/shared/src/services/telematicsDriverTripsApi';

/** "15 January 2026". Built once; formatter construction is expensive. */
const LONG_DATE_FORMATTER = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

/**
 * Returns a human-readable date label: "Today", "Yesterday", or a long date
 * such as "15 January 2026".
 */
export const getDateLabel = (date: Date): string => {
	if (isToday(date)) return 'Today';
	if (isYesterday(date)) return 'Yesterday';
	return LONG_DATE_FORMATTER.format(date);
};

/**
 * Groups trips by their date label, preserving the input order (most recent first).
 * Trips without a start time are skipped.
 */
export const groupTripsByDate = (trips: TripSummaryDto[]): Map<string, TripSummaryDto[]> => {
	const grouped = new Map<string, TripSummaryDto[]>();

	for (const trip of trips) {
		if (!trip.startTimeUtc) continue;

		const dateLabel = getDateLabel(new Date(trip.startTimeUtc));
		const group = grouped.get(dateLabel);

		if (group) {
			group.push(trip);
		} else {
			grouped.set(dateLabel, [trip]);
		}
	}

	return grouped;
};

/**
 * Formats a date to time string: "07:00am"
 *
 * @param date - The date to format
 * @returns Formatted time string in "hh:mmaaa" format (e.g., "07:00am", "02:30pm"), or empty string if invalid
 */
export const formatTripTime = (date: Date): string => {
	const d = new Date(date);
	if (!isValid(d)) return '';
	return format(d, 'hh:mmaaa');
};

/**
 * Formats duration in seconds to readable string: "11mins" or "1h 30mins"
 */
export const formatDuration = (seconds: number): string => {
	if (!seconds || seconds <= 0) return '0mins';

	const minutes = Math.floor(seconds / 60);

	if (minutes < 60) {
		return `${minutes}mins`;
	}

	const hours = Math.floor(minutes / 60);
	const remainingMinutes = minutes % 60;

	if (remainingMinutes === 0) {
		return `${hours}h`;
	}

	return `${hours}h ${remainingMinutes}mins`;
};

/**
 * Formats distance in miles: "4miles"
 */
export const formatDistance = (miles: number): string => {
	if (!miles || miles <= 0) return '0miles';
	return `${Math.round(miles)}miles`;
};

/**
 * Normalizes a score from -10 to 10 range to 0-100 range
 * -10 → 0, 0 → 50, 10 → 100
 */
export const normalizeScore = (score: number): number => {
	if (score === undefined || score === null) return 0;
	// Clamp to valid range
	const clampedScore = Math.max(-10, Math.min(10, score));
	return Math.round(((clampedScore + 10) / 20) * 100);
};

/**
 * Returns the appropriate theme color based on a trip score value.
 *
 * Uses different thresholds than driver scores (80/60 vs 70/50) to account
 * for the normalized trip score scale.
 *
 * Color thresholds:
 * - score >= 80: success (green)
 * - score >= 60: warning (amber)
 * - score < 60: error (red)
 *
 * @param score - Normalized score value (0-100)
 * @param theme - MUI theme object for accessing palette colors
 * @returns Theme color string from the palette
 *
 * @example
 * ```ts
 * const color = getTripScoreColor(85, theme); // Returns theme.palette.success.main
 * const color = getTripScoreColor(65, theme); // Returns theme.palette.warning.main
 * ```
 */
export const getTripScoreColor = (score: number, theme: Theme): string => {
	if (score >= 80) return theme.palette.success.main;
	if (score >= 60) return theme.palette.warning.main;
	return theme.palette.error.main;
};
